import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { url, LOGIN_EMAIL, LOGIN_GOOGLE, ME, LOGOUT } from "../api/paths";

const TOKEN_KEY = "runity_token";
const USER_KEY = "runity_user";

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
    const appId = (typeof process !== 'undefined' && process.env && process.env.GATSBY_APP_ID) || (typeof window !== 'undefined' && window.__GATSBY_APP_ID) || '';
    if (!appId && typeof window !== 'undefined') {
      console.warn('GATSBY_APP_ID is missing; AppId header will be empty');
    }
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const t = localStorage.getItem(TOKEN_KEY);
    const u = localStorage.getItem(USER_KEY);
    setToken(t || null);
    setUser(u ? JSON.parse(u) : null);
    setIsLoading(false);
  }, []);

  const persist = (nextToken, nextUser) => {
    if (typeof window === "undefined") return;
    if (nextToken) localStorage.setItem(TOKEN_KEY, nextToken); else localStorage.removeItem(TOKEN_KEY);
    if (nextUser) localStorage.setItem(USER_KEY, JSON.stringify(nextUser)); else localStorage.removeItem(USER_KEY);
  };

  const fetchMe = async (bearer) => {
    const res = await fetch(url(ME), {
      method: "GET",
      headers: { accept: "application/json", Authorization: `Bearer ${bearer}`, AppId: appId },
    });
    if (!res.ok) throw new Error(`GET /me failed: ${res.status}`);
    const me = await res.json();
    setUser(me);
    persist(bearer, me);
  };

  const loginWithToken = async (accessToken) => {
    setToken(accessToken);
    await fetchMe(accessToken);
  };

  const decodeEmailFromIdToken = (token) => {
    try {
      const base64 = token.split(".")[1];
      const payload = JSON.parse(atob(base64.replace(/-/g, "+").replace(/_/g, "/")));
      return payload.email || "";
    } catch { return ""; }
  };

  const getHardwareId = () => {
    if (typeof window === 'undefined') return '';
    let hw = localStorage.getItem('runity_hardware_id');
    if (!hw) {
      hw = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2,10)}`;
      try { localStorage.setItem('runity_hardware_id', hw); } catch {}
    }
    return hw;
  };

  const loginWithGoogle = async (idToken) => {
    const email = idToken ? decodeEmailFromIdToken(idToken) : "";
    const hardwareId = getHardwareId();
    const body = idToken ? { idToken, email, hardwareId } : {};
    const res = await fetch(url(LOGIN_GOOGLE), {
      method: "POST",
      headers: { "Content-Type": "application/json", accept: "application/json", AppId: appId },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`POST login-gmail failed: ${res.status}`);
    const data = await res.json();
    await loginWithToken(data.accessToken);
  };

  const loginWithEmailPassword = async (email, password) => {
    const res = await fetch(url(LOGIN_EMAIL), {
      method: "POST",
      headers: { "Content-Type": "application/json", accept: "application/json", AppId: appId },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) throw new Error(`POST login failed: ${res.status}`);
    const data = await res.json();
    await loginWithToken(data.accessToken);
  };

  const logout = async () => {
    try {
      await fetch(url(LOGOUT), { method: "POST", headers: { AppId: appId } });
    } catch {}
    persist(null, null);
    setToken(null);
    setUser(null);
    if (typeof window !== "undefined") {
      try { window.google?.accounts?.id?.disableAutoSelect?.(); } catch {}
      window.location.assign("/");
    }
  };

  const value = useMemo(() => ({
    token, user, isLoading,
    loginWithGoogle,
    loginWithEmailPassword,
    loginWithToken,
    fetchMe,
    logout,
  }), [token, user, isLoading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
